/*
 * Copyright (c) 2024, King Living
 * Cart Flyout Component
 *
 * Displays cart items in a Drawer flyout when cart icon is clicked
 */

import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {useProducts} from '@salesforce/commerce-sdk-react';
import {BrandLogo} from '@amblique/king-living/app/components/icons';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import OrderSummary from '@salesforce/retail-react-app/app/components/order-summary';
import ProductItem from '@salesforce/retail-react-app/app/components/product-item';
import CartCta from '@salesforce/retail-react-app/app/pages/cart/partials/cart-cta';
import CartSecondaryButtonGroup
  from '@salesforce/retail-react-app/app/pages/cart/partials/cart-secondary-button-group';
import EmptyCart from '@salesforce/retail-react-app/app/pages/cart/partials/empty-cart';
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerCloseButton,
  Flex,
  Heading,
  Stack,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import SwipeableItem from './swipeable-item';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {
  API_ERROR_MESSAGE,
  TOAST_MESSAGE_REMOVED_ITEM_FROM_CART,
} from '@salesforce/retail-react-app/app/constants';
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react';
import debounce from 'lodash/debounce';

const DEBOUNCE_WAIT = 750;

/**
 * Cart Flyout Component
 * Displays cart items in a Drawer flyout
 */
const CartFlyout = ({
  isOpen,
  onClose,
  onOpen,
  afterOpen,
  beforeClose,
  afterClose,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('CartFlyout', {});
  const toast = useToast();
  const navigate = useNavigation();
  const {data: basket} = useCurrentBasket();
  const {data: customer} = useCurrentCustomer();
  const {isRegistered} = customer || {};

  const drawerSize = useBreakpointValue({base: 'full', lg: '490px'});
  const isMobile = useBreakpointValue({base: true, lg: false});

  // Calculate total items
  const totalItems = useMemo(() => {
    return basket?.productItems?.reduce((acc, item) => acc + item.quantity, 0) || 0;
  }, [basket?.productItems]);

  // Memoize cart title and item count text
  const cartTitle = useMemo(() => {
    return intl.formatMessage({
      defaultMessage: 'Your Cart',
      id: 'cart_flyout.title.your_cart',
    });
  }, [intl]);

  const itemCountText = useMemo(() => {
    return intl.formatMessage(
      {
        defaultMessage: '{itemCount, plural, =0 {0 items} one {# item} other {# items}}',
        id: 'cart_flyout.title.item_count',
      },
      {itemCount: totalItems}
    );
  }, [intl, totalItems]);

  // Error handling
  const showError = useCallback(() => {
    toast({
      title: intl.formatMessage(API_ERROR_MESSAGE),
      status: 'error',
    });
  }, [intl, toast]);


  // Memoize edit click handler
  const handleEditClick = useCallback(() => {
    navigate('/cart');
  }, [navigate]);

  // Refs for cleanup
  const debouncedChangeQuantityRef = useRef();
  const afterCloseTimeoutRef = useRef();

  // Fetch products for cart items - memoize to prevent unnecessary recalculations
  const productIds = useMemo(() => {
    return basket?.productItems?.map(({productId}) => productId).join(',') ?? '';
  }, [basket?.productItems]);
  const {data: products, isLoading: isProductsLoading} = useProducts(
    {
      parameters: {
        ids: productIds,
        allImages: true,
        perPricebook: true,
      },
    },
    {
      enabled: Boolean(productIds),
      select: (result) => {
        return result?.data?.reduce((result, item) => {
          const key = item.id;

          result[key] = item;

          return result;
        }, {});
      },
    }
  );

  // Products by itemId for bundle support
  const productsByItemId = useMemo(() => {
    if (!products || !basket?.productItems) {
      return {};
    }

    return basket.productItems.reduce((acc, productItem) => {
      const product = products[productItem.productId];

      if (product) {
        acc[productItem.itemId] = product;
      }

      return acc;
    }, {});
  }, [products, basket?.productItems]);

  // Local state for quantity updates
  const [localQuantity, setLocalQuantity] = React.useState({});
  const [selectedItem, setSelectedItem] = React.useState();
  const [isCartItemLoading, setCartItemLoading] = React.useState(false);

  // Mutations
  const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket');
  const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket');

  // Handle remove item - defined early since it's used by other handlers
  const handleRemoveItem = useCallback(async (product) => {
    setSelectedItem(product);

    try {
      await removeItemFromBasketMutation.mutateAsync(
        {
          parameters: {basketId: basket?.basketId, itemId: product.itemId},
        },
        {
          onSuccess: () => {
            toast({
              title: intl.formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
              status: 'success',
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: intl.formatMessage(API_ERROR_MESSAGE),
        status: 'error',
      });
      throw error; // Re-throw to allow caller to handle
    } finally {
      setSelectedItem(undefined);
    }
  }, [basket?.basketId, intl, toast, removeItemFromBasketMutation]);

  // Memoize swipe delete handler
  const handleSwipeDelete = useCallback((productItem) => {
    handleRemoveItem(productItem);
  }, [handleRemoveItem]);

  // Memoize secondary actions component to prevent re-renders (shared across all items)
  const secondaryActionsComponent = useMemo(() => (
    <CartSecondaryButtonGroup
      onEditClick={handleEditClick}
      onRemoveItemClick={handleRemoveItem}
    />
  ), [handleEditClick, handleRemoveItem]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      // Store original overflow values
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalOverflowY = window.getComputedStyle(document.body).overflowY;
      const originalPaddingRight = window.getComputedStyle(document.body).paddingRight;

      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.overflowY = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      // Restore original styles on cleanup
      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.overflowY = originalOverflowY;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  // Lifecycle hooks
  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  }, [isOpen, onOpen]);

  useEffect(() => {
    if (isOpen && afterOpen) {
      // Small delay to ensure drawer is fully open
      const timer = setTimeout(() => {
        afterOpen();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, afterOpen]);

  // Create debounced function with cleanup
  useEffect(() => {
    debouncedChangeQuantityRef.current = debounce(async (quantity, product) => {
      // This local state allows the dropdown to show the desired quantity
      // while the API call to update it is happening.
      const previousQuantity = localQuantity[product.itemId];

      setLocalQuantity((prev) => ({...prev, [product.itemId]: quantity}));
      setCartItemLoading(true);
      setSelectedItem(product);

      await updateItemInBasketMutation.mutateAsync(
        {
          parameters: {basketId: basket?.basketId, itemId: product.itemId},
          body: {
            productId: product.id,
            quantity: parseInt(quantity),
          },
        },
        {
          onSettled: () => {
            // reset the state
            setCartItemLoading(false);
            setSelectedItem(undefined);
          },
          onSuccess: () => {
            setLocalQuantity((prev) => {
              const updated = {...prev};

              delete updated[product.itemId];

              return updated;
            });
          },
          onError: () => {
            // reset the quantity to the previous value
            setLocalQuantity((prev) => ({...prev, [product.itemId]: previousQuantity}));
            showError();
          },
        }
      );
    }, DEBOUNCE_WAIT);

    return () => {
      debouncedChangeQuantityRef.current?.cancel();
    };
  }, [basket?.basketId, localQuantity, updateItemInBasketMutation, showError]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (afterCloseTimeoutRef.current) {
        clearTimeout(afterCloseTimeoutRef.current);
      }
    };
  }, []);

  const handleChangeItemQuantity = useCallback(async (product, value) => {
    const stockLevel = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1;

    // Handle removing of the items when 0 is selected.
    if (value === 0) {
      // Flush last call to keep ui in sync with data.
      debouncedChangeQuantityRef.current?.flush();

      // Set the selected item to the current product to the modal acts on it.
      setSelectedItem(product);

      // Call remove item handler
      await handleRemoveItem(product);

      // Return false as 0 isn't valid section.
      return false;
    }

    // Cancel any pending handlers.
    debouncedChangeQuantityRef.current?.cancel();

    // Allow use to selected values above the inventory.
    if (value > stockLevel || value === product.quantity) {
      return true;
    }

    // Take action.
    debouncedChangeQuantityRef.current?.(value, product);

    return true;
  }, [productsByItemId, handleRemoveItem]);

  // Handle close with lifecycle hooks
  const handleClose = useCallback(() => {
    if (beforeClose) {
      beforeClose();
    }

    onClose();

    if (afterClose) {
      // Small delay to ensure drawer is fully closed
      afterCloseTimeoutRef.current = setTimeout(() => {
        afterClose();
      }, 100);
    }
  }, [beforeClose, onClose, afterClose]);

  return (
    <Drawer
      blockScrollOnMount={true}
      isOpen={isOpen}
      placement="right"
      size={drawerSize}
      onClose={handleClose}
    >
      <DrawerOverlay {...styles.overlay} />
      <DrawerContent {...styles.content}>
        <DrawerHeader {...styles.header}>
          <Stack spacing={3} width="100%">
            {/* Mobile: Logo and Close button row */}
            <HideOnDesktop>
              <Flex alignItems="center" justifyContent="space-between" width="100%">
                <Box {...styles.logo} sx={styles.logoSvg}>
                  <BrandLogo />
                </Box>
                <DrawerCloseButton />
              </Flex>
            </HideOnDesktop>
            {/* Desktop: Title and Close button row */}
            <Box display={{base: 'none', lg: 'block'}}>
              <Flex alignItems="center" justifyContent="space-between" width="100%">
                <Flex alignItems="center" flex={1} justifyContent="space-between">
                  <Heading as="h2" {...styles.title}>
                    {cartTitle}
                  </Heading>
                  <Text {...styles.itemCount}>
                    {itemCountText}
                  </Text>
                </Flex>
                <DrawerCloseButton />
              </Flex>
            </Box>
            {/* Mobile: Heading row */}
            <HideOnDesktop>
              <Flex alignItems="center" justifyContent="space-between" width="100%">
                <Heading as="h2" {...styles.title}>
                  {cartTitle}
                </Heading>
                <Text {...styles.itemCount}>
                  {itemCountText}
                </Text>
              </Flex>
            </HideOnDesktop>
          </Stack>
        </DrawerHeader>
        <DrawerBody {...styles.body} padding={0}>
          <Stack height="100%" spacing={0}>
            {/* Cart Items List */}
            <Box {...styles.itemsContainer}>
              {!basket?.productItems?.length ? (
                <EmptyCart isRegistered={isRegistered} />
              ) : isProductsLoading ? (
                <Box {...styles.loadingState}>
                  <Text color="gray.600" fontSize="sm">
                    <FormattedMessage
                      defaultMessage="Loading cart items..."
                      id="cart_flyout.loading"
                    />
                  </Text>
                </Box>
              ) : (
                <Stack padding={4} spacing={4} sx={styles.itemsList}>
                  {basket.productItems?.map((productItem, index) => {
                    // Create product object (will be stable if dependencies don't change)
                    const product = {
                      ...productItem,
                      ...(productsByItemId?.[productItem.itemId]),
                      isProductUnavailable: !isProductsLoading ?
                        !productsByItemId?.[productItem.itemId] :
                        undefined,
                      price: productItem.price,
                      quantity: localQuantity[productItem.itemId] || productItem.quantity,
                    };

                    // Use memoized secondary actions component
                    const secondaryActions = secondaryActionsComponent;

                    const productItemComponent = (
                      <ProductItem
                        key={productItem.itemId}
                        className="cart-item"
                        product={product}
                        secondaryActions={secondaryActions}
                        showLoading={
                          isCartItemLoading &&
                          selectedItem?.itemId === productItem.itemId
                        }
                        onItemQuantityChange={handleChangeItemQuantity.bind(
                          this,
                          productItem
                        )}
                      />
                    );

                    // Wrap in SwipeableItem on mobile
                    if (isMobile) {
                      // Create swipe delete handler for this specific item
                      const handleItemSwipeDelete = () => {
                        handleSwipeDelete(productItem);
                      };

                      return (
                        <SwipeableItem
                          key={productItem.itemId}
                          isFirstItem={index === 0}
                          onSwipeDelete={handleItemSwipeDelete}
                        >
                          {productItemComponent}
                        </SwipeableItem>
                      );
                    }

                    return productItemComponent;
                  })}
                </Stack>
              )}
            </Box>

            {/* Order Summary */}
            <Box {...styles.summaryContainer}>
              <Stack spacing={4}>
                <OrderSummary
                  basket={basket}
                  isEstimate={true}
                  showPromoCodeForm={false}
                />
                <CartCta />
              </Stack>
            </Box>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

CartFlyout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  afterOpen: PropTypes.func,
  beforeClose: PropTypes.func,
  afterClose: PropTypes.func,
};

export default CartFlyout;
